<template>
  <div
    class="min-h-screen flex bg-[#f8fafc] font-sans text-slate-700 antialiased selection:bg-blue-500 selection:text-white">

    <!-- Sidebar / Navigation principale -->
    <aside v-if="!['/login', '/waiting-room', '/inscription'].includes($route.path)"
      class="w-64 bg-white border-r border-slate-200/60 flex flex-col hidden lg:flex min-h-screen p-5 shrink-0">
      <!-- Logo du Cabinet -->
      <div class="flex items-center gap-3 px-2 py-3 mb-4">
        <div
          class="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center text-white font-extrabold text-lg shadow-md shadow-blue-500/20">
          <!-- Icône style Mediczen -->
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4" />
          </svg>
        </div>
        <span class="font-bold text-slate-900 text-lg tracking-tight">DATAFUSE<span
            class="text-blue-600">™</span></span>
      </div>

      <!-- Barre de recherche globale sidebar -->
      <div class="mb-6 relative">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input type="text" placeholder="Search"
          class="w-full pl-9 pr-3 py-2 bg-slate-100/80 border border-transparent rounded-xl text-xs text-slate-700 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-500/30 transition-all" />
      </div>

      <!-- Navigation par catégories -->
      <div class="flex-1 space-y-6 overflow-y-auto">
        <!-- Section MAIN -->
        <div>
          <div class="px-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Main</div>
          <ul class="space-y-1">
            <li>
              <router-link to="/"
                class="flex items-center gap-3 py-2.5 px-3 rounded-xl text-xs font-semibold text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-all"
                active-class="!bg-blue-50 !text-blue-600 font-bold">
                <i class="fa-solid fa-house text-sm w-5 text-center"></i>
                Dashboard
              </router-link>
            </li>
            <li>
              <router-link to="/AdminPatient"
                class="flex items-center gap-3 py-2.5 px-3 rounded-xl text-xs font-semibold text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-all"
                active-class="!bg-blue-50 !text-blue-600 font-bold">
                <i class="fa-solid fa-user-group text-sm w-5 text-center"></i>
                Patients
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Section SUPPORT & CONFIG -->
        <div>
          <div class="px-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Support</div>
          <ul class="space-y-1">
            <li>
              <router-link to="/AdminParametre"
                class="flex items-center gap-3 py-2.5 px-3 rounded-xl text-xs font-semibold text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-all"
                active-class="!bg-blue-50 !text-blue-600 font-bold">
                <i class="fa-solid fa-gear text-sm w-5 text-center"></i>
                Settings
              </router-link>
            </li>
          </ul>
        </div>
      </div>

     <!-- Widget Parrainage Confrère -->
      <div class="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100/80 rounded-2xl p-3 mb-3 text-slate-700">
        <div class="flex items-center gap-2 font-bold text-xs text-indigo-900 mb-1">
          <i class="fa-solid fa-user-doctor text-indigo-600"></i>
          <span>Parrainage Confrère</span>
        </div>
        <p class="text-[11px] text-slate-500 leading-relaxed mb-2">
          Recommandez notre logiciel & site sur mesure et gagnez <strong class="text-indigo-700">10% du montant total du devis</strong> pour chaque confrère parrainé.
        </p>
        <button class="w-full py-1.5 px-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-[11px] font-bold shadow-sm transition-all">
          Parrainer un confrère
        </button>
      </div>

      <!-- Widget Avis Google & Trustpilot -->
      <div class="bg-slate-50 border border-slate-200/60 rounded-2xl p-3 mb-3">
        <div class="text-[11px] font-bold text-slate-700 mb-2 flex items-center justify-between">
          <span>Votre avis compte</span>
          <span class="text-amber-500 text-xs">★★★★★</span>
        </div>
        <div class="grid grid-cols-2 gap-2">
          <a href="https://g.page/r/CSYUETCIqbJeEAE/review" target="_blank" 
             class="flex items-center justify-center gap-1.5 py-1.5 px-2 bg-white border border-slate-200/80 hover:bg-slate-100 rounded-xl text-[10px] font-bold text-slate-700 transition-colors shadow-2xs">
            <i class="fa-brands fa-google text-blue-500"></i> Google
          </a>
          <a href="https://fr.trustpilot.com/review/datafuse.fr" target="_blank" 
             class="flex items-center justify-center gap-1.5 py-1.5 px-2 bg-white border border-slate-200/80 hover:bg-slate-100 rounded-xl text-[10px] font-bold text-slate-700 transition-colors shadow-2xs">
            <i class="fa-solid fa-star text-emerald-600"></i> Trustpilot
          </a>
        </div>
      </div>

      <!-- Bouton Logout -->
      <div class="pt-2 border-t border-slate-100">
        <button @click="handleLogout"
          class="w-full flex items-center gap-3 py-2.5 px-3 rounded-xl text-xs font-semibold text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-all">
          <i class="fa-solid fa-right-from-bracket text-sm w-5 text-center"></i>
          Déconnexion
        </button>
      </div>
    </aside>

    <!-- Zone de contenu avec Header Supérieur -->
    <div class="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">

      <!-- Topbar Header (Dr. Thierry Paradis) -->
      <header v-if="!['/login', '/waiting-room', '/inscription'].includes($route.path)"
        class="h-16 bg-white/80 backdrop-blur-md border-b border-slate-200/60 px-8 flex items-center justify-between shrink-0 z-10">
        <div>
          <h1 class="text-base font-bold text-slate-900 tracking-tight">Bonjour, Dr. Thierry Paradis 👋</h1>
          <p class="text-[11px] text-slate-400">Bienvenue sur votre espace de consultation médicale</p>
        </div>

        <div class="flex items-center gap-4">
          <!-- Icônes d'actions (Paramètres rapides, Notifications, Profil) -->
          <button
            class="w-9 h-9 rounded-xl bg-slate-100/80 hover:bg-slate-200/60 text-slate-600 flex items-center justify-center transition-colors">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            </svg>
          </button>

          <button
            class="w-9 h-9 rounded-xl bg-slate-100/80 hover:bg-slate-200/60 text-slate-600 flex items-center justify-center transition-colors relative">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <span class="w-2 h-2 rounded-full bg-blue-600 absolute top-2 right-2 border-2 border-white"></span>
          </button>

          <div class="h-8 w-px bg-slate-200/80 mx-1"></div>

          <!-- Avatar Médecin -->
          <div class="flex items-center gap-3">
            <div
              class="w-9 h-9 rounded-xl bg-blue-100 border border-blue-200 text-blue-700 font-bold flex items-center justify-center text-xs overflow-hidden">
              TP
            </div>
          </div>
        </div>
      </header>

      <!-- Affichage de la page (Dashboard, Patients, etc.) -->
      <main class="flex-1 overflow-y-auto">
        <router-view />
      </main>

    </div>
  </div>
</template>

<script>
export default {
  name: 'App',
  methods: {
    handleLogout() {
      localStorage.removeItem('auth_token');
      this.$router.push('/login');
    }
  }
}
</script>

<style>
#app {
  font-family: 'Plus Jakarta Sans', 'Inter', system-ui, -apple-system, sans-serif;
  color: #1e293b;
}

/* Personnalisation de la barre de défilement pour rester très discrète */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 9999px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>